import { MiscUtil } from '../utility/MiscUtil';
import { TypeUtil } from '../utility/TypeUtil';

class WindowHelper {
  private static requestAnimationFrameFunc: (_: () => void) => number = undefined;
  private static cancelAnimationFrameFunc: (_: number) => void = undefined;

  private static get Selection(): Selection {
    if (TypeUtil.HasMethod(/*typeof*/window, 'getSelection')) {
      return window.getSelection();
    }
    if (TypeUtil.HasMethod(/*typeof*/document, 'getSelection')) {
      return document.getSelection();
    }
    return null;
  }

  /// <summary>
  /// Clears the selection object at the window/document level
  /// </summary>
  public static ClearSelection(): void {
    let selection: Selection = this.Selection;
    if (!MiscUtil.IsNullOrUndefined(selection)) {
      if (TypeUtil.HasMethod(selection, 'removeAllRanges')) {
        selection.removeAllRanges();
      } else if (TypeUtil.HasMethod(selection, 'empty')) {
        selection.empty();
      }
    }
  }

  /// <summary>
  /// Requests an animation frame
  /// Falls back to using Window.SetTimeout if the browser does not support RequestAnimationFrame
  /// </summary>
  /// <param name="action">Action to execute</param>
  /// <returns>animation id used to cancel the animation</returns>
  public static RequestAnimationFrame(action: () => void): number {
    return WindowHelper.requestAnimationFrameFunc(action);
  }

  /// <summary>
  /// Cancels an animation
  /// </summary>
  /// <param name="animationId">id of animation to cancel</param>
  public static CancelAnimationFrame(animationId: number): void {
    if (!MiscUtil.IsNullOrUndefined(animationId)) {
      WindowHelper.cancelAnimationFrameFunc(animationId);
    }
  }

  private static SetDefaultRequestAnimationFrameImpl(): void {
    let lastTime: number = 0;
    WindowHelper.requestAnimationFrameFunc = callback => {
      const curTime: number = Date.now();
      const timeToCall: number = Math.max(0, 16 - (curTime - lastTime));
      lastTime = curTime + timeToCall;
      const id: number = window.setTimeout(callback, timeToCall);
      return id;
    };
  }

  public static PolyFillRequestAnimationFrame(): void {
    // Polyfill for RequestAnimationFrame - http://paulirish.com/2011/requestanimationframe-for-smart-animating/
    const DefaultRequestName: string = 'requestAnimationFrame';
    const DefaultCancelName: string = 'cancelAnimationFrame';
    const vendors: string[] = ['ms', 'moz', 'webkit', 'o'];
    let requestFuncName: string = null;
    let cancelFuncName: string = null;

    if (TypeUtil.HasMethod(window, DefaultRequestName)) {
      requestFuncName = DefaultRequestName;
    }

    if (TypeUtil.HasMethod(window, DefaultCancelName)) {
      cancelFuncName = DefaultCancelName;
    }

    for (let ii: number = 0; ii < vendors.length && (requestFuncName === null || cancelFuncName === null); ++ii) {
      const vendor: string = vendors[ii];
      let funcName: string = vendor + 'RequestAnimationFrame';
      if (requestFuncName === null && TypeUtil.HasMethod(window, funcName)) {
        requestFuncName = funcName;
      }

      if (cancelFuncName === null) {
        funcName = vendor + 'CancelAnimationFrame';
        if (TypeUtil.HasMethod(window, funcName)) {
          cancelFuncName = funcName;
        }
        funcName = vendor + 'CancelRequestAnimationFrame';
        if (TypeUtil.HasMethod(window, funcName)) {
          cancelFuncName = funcName;
        }
      }
    }

    if (requestFuncName !== null) {
      WindowHelper.requestAnimationFrameFunc
        = callback => (TypeUtil.GetField(window, requestFuncName) as (_: () => void) => number)(callback);
    } else {
      this.SetDefaultRequestAnimationFrameImpl();
    }

    if (cancelFuncName !== null) {
      WindowHelper.cancelAnimationFrameFunc
        = animationId => (TypeUtil.GetField(window, cancelFuncName) as (_: number) => void)(animationId);
    } else {
      WindowHelper.cancelAnimationFrameFunc = window.clearTimeout;
    }
  }
}

// initialize
WindowHelper.PolyFillRequestAnimationFrame();

export { WindowHelper };

